/* eslint @typescript-eslint/strict-boolean-expressions: off */

import {
  useCurrentDate,
  getTimeRelative,
  formatDistanceStrictMinimal,
  TimeRelative,
} from "gui/time/time";
import {
  resultStatusError,
  resultStatusIcon,
  resultStatusTextLong,
  resultStatusType,
} from "lib/results/status";
import { QueryVisual } from "lib/visuals/types";
import React from "react";
import { VisualProps } from "./visual";
import styles from "gui/visual/status.module.css";
import { DialogText } from "gui/dialog/text";
import { QueryScheduleText } from "gui/editor/EditorTitle/QueryScheduleText";
import { Badge } from "components/Badge/Badge";
import { IconCircleWavyCheck } from "components/Icons/IconCircleWavyCheck";
import { IconWarning } from "components/Icons/IconWarning";
import { IconLoading } from "components/Icons/IconLoading";

export const StatusIcon: React.FC<VisualProps<QueryVisual>> = (props) => {
  const statusType = resultStatusType(props.result);

  if (statusType === "unknown") {
    return null;
  }
  switch (statusType) {
    case "errorWithStaleResult":
      return <ErrorWithStaleResultsContent {...props} />;
    default:
      return <DefaultContent {...props} />;
  }
};

const ErrorWithStaleResultsContent: React.FC<VisualProps<QueryVisual>> = (
  props
) => {
  const [dialog, setDialog] = React.useState(false);
  const now = useCurrentDate();
  const resultTimeObject = getTimeRelative({
    now,
    time: props.result?.meta?.generated_at,
  });
  const resultTime = resultTimeObject?.rel.toLocaleLowerCase();
  const errorTime = getTimeRelative({
    now,
    time: props.result?.error?.generated_at,
  })?.rel.toLocaleLowerCase();
  const timeText = resultTime && `Last updated ${resultTime}.`;

  return (
    <>
      <span className={styles.status}>
        <button onClick={() => setDialog((open) => !open)}>
          <Badge size="M" variant="filled" color="warning">
            {resultTimeObject &&
              formatDistanceStrictMinimal({
                now,
                date: resultTimeObject.iso,
              })}
            <IconWarning />
          </Badge>
        </button>

        {props.iframe && (
          <img
            src="/assets/glyph.svg"
            alt="Dune logo"
            className={styles.duneLogo}
          />
        )}
      </span>

      <DialogText
        icon={"exclamation-circle"}
        isOpen={dialog}
        className={styles.dialog}
        onDismiss={() => setDialog((prev) => !prev)}
        label="Result status"
        size="sm"
      >
        <ul>
          <li key={timeText}>
            <strong>{timeText}</strong>
          </li>
        </ul>
        <p>
          The most recent execution, {errorTime}, failed with the following
          error:
        </p>
        <p className={styles.errorMessage}>{resultStatusError(props.result)}</p>
        <p>
          Showing the most recent successful result from:{" "}
          <strong>{resultTime}</strong>
        </p>
        <p>
          <QueryScheduleText />
        </p>
      </DialogText>
    </>
  );
};

const DefaultContent: React.FC<VisualProps<QueryVisual>> = (props) => {
  const [dialog, setDialog] = React.useState(false);
  const statusText = resultStatusTextLong(props.result);
  const now = useCurrentDate();
  const time = getTimeRelative({
    now,
    time: props.result?.meta?.generated_at || props.result?.error?.generated_at,
  });
  const statusIcon = resultStatusIcon(props.result);
  const timeText = time && `Last updated ${time.rel.toLowerCase()}.`;
  const text = [timeText, statusText].filter(Boolean);

  if (text.length === 0) {
    return null;
  }

  if (props.iframe) {
    return (
      <span title={text.join(" ")} className={styles.status}>
        <img
          src="/assets/glyph.svg"
          alt="Dune logo"
          className={styles.duneLogo}
        />
      </span>
    );
  }

  if (time === undefined) {
    return null;
  }

  function renderBadge(time: TimeRelative) {
    if (statusIcon === "running") {
      return (
        <Badge size="M" variant="filled" color="brand-orange">
          Running
          <IconLoading />
        </Badge>
      );
    }

    return (
      <Badge size="M" variant="filled" color="success">
        {formatDistanceStrictMinimal({
          now,
          date: time.iso,
        })}
        <IconCircleWavyCheck />
      </Badge>
    );
  }

  return (
    <>
      <span className={styles.status}>
        <button onClick={() => setDialog((prev) => !prev)}>
          {renderBadge(time)}
        </button>
      </span>

      <DialogText
        icon={statusIcon}
        className={styles.dialog}
        isOpen={dialog}
        onDismiss={() => setDialog((prev) => !prev)}
        label="Result status"
        size="sm"
      >
        <ul>
          {text.map((t) => (
            <li key={t}>
              <strong>{t}</strong>
            </li>
          ))}
        </ul>
        <p>
          <QueryScheduleText />
        </p>
      </DialogText>
    </>
  );
};
